"use client";

/**
 * <RefCapture/> — partner-program deep-link capture + post-signin claim.
 *
 * Mounted in `app/layout.tsx`. Two responsibilities:
 *
 *  1. Pre-signin: read `?ref=<slug>` from the URL, POST to /v1/referral/track
 *     so the b3_ref cookie is set on .b3os.org. Strip the param via
 *     replaceState so analytics stays clean.
 *
 *  2. Post-signin: once the user is authenticated AND a b3_ref cookie is
 *     present, POST to /v1/referral/attribute to UPSERT the
 *     partner_referral_attributions row (the activation credit fires later
 *     on the user's first successful workflow run). Idempotent — safe to
 *     re-fire on every authenticated mount.
 *
 * Returns null — this is a pure side-effect component. Never gates rendering of
 * children (per b3os-web SSR rule).
 *
 * See services/b3os-workflow/docs/PARTNER_PROGRAM.md §3 (Attribution Flow) and §10.
 */
import { Suspense, useEffect, useRef } from "react";
import { useSearchParams } from "next/navigation";
import Cookies from "js-cookie";
import { B3_AUTH_COOKIE_NAME } from "@b3dotfun/sdk/shared/constants";

// Frontend gate is intentionally looser than the backend (which enforces
// {4,32}) — short slugs (?ref=ab) pass this regex, get rejected by the
// partner-service, and the ?ref= param stays in the URL so a corrected slug
// retries on the next navigation. Tightening here would silently swallow
// typos before the user sees them.
const SLUG_REGEX = /^[A-Za-z0-9_-]{1,32}$/;

// Partner-service is the home of /v1/referral/track and /v1/referral/attribute
// post-v2 decoupling. Falls through to the workflow API endpoint if
// NEXT_PUBLIC_PARTNER_ENDPOINT isn't set yet (e.g. during the v1→v2 rollout
// window before the new env var lands in Doppler).
const API_BASE = process.env.NEXT_PUBLIC_PARTNER_ENDPOINT ?? process.env.NEXT_PUBLIC_V2_ENDPOINT ?? "";

// useSearchParams must be inside a Suspense boundary in Next.js — wrap to keep
// the host page's static rendering intact while we read URL params client-side.
export function RefCapture(): React.ReactElement {
  return (
    <Suspense fallback={null}>
      <RefCaptureInner />
    </Suspense>
  );
}

function RefCaptureInner(): React.ReactElement | null {
  const searchParams = useSearchParams();
  // useSearchParams returns a fresh object reference on every navigation, so
  // depending on it directly would re-run this effect on every route change
  // even when there's no `ref` param. Project the only field we care about
  // to a stable string so the effect only re-runs when the slug actually
  // changes — and only does work for navigations that carry a `?ref=`.
  const refSlug = searchParams?.get("ref") ?? "";
  // sentSlugRef guards against double-fire: replaceState mutates the URL and
  // some Next.js router builds may re-run this effect with stale searchParams.
  const sentSlugRef = useRef<string | null>(null);

  // 1. Deep-link capture: ?ref=<slug> → /v1/referral/track → b3_ref cookie set.
  //
  // The actual b3_ref cookie is httpOnly so JS can't read it; we set a
  // companion non-httpOnly `b3_ref_seen=<slug>` cookie on the client to
  // skip the POST when the same visitor already tracked the same slug. The
  // backend remains idempotent — this just avoids redundant network traffic
  // on every deep-link share. A new slug clears the marker by overwriting.
  useEffect(() => {
    if (!refSlug || !SLUG_REGEX.test(refSlug) || sentSlugRef.current === refSlug) {
      return;
    }
    if (Cookies.get("b3_ref_seen") === refSlug) {
      sentSlugRef.current = refSlug;
      return;
    }
    sentSlugRef.current = refSlug;
    // `credentials: "include"` is REQUIRED here even though the handler does
    // not read auth — `/v1/referral/track` SETS the `b3_ref` cookie on
    // .b3os.org via the response, and cross-origin (b3os-web → workflow API
    // on api.b3os.org) Set-Cookie is silently dropped by the browser unless
    // the originating fetch was credentialed. Switching to "same-origin"
    // would break referral capture in any deployment where API_BASE is a
    // sibling domain. The auth cookie tags along but the handler ignores it.
    fetch(`${API_BASE}/v1/referral/track`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ slug: refSlug }),
    })
      .then(res => {
        if (res.ok) {
          // Mirror the backend cookie's typical 60-day window. Companion
          // marker is best-effort; losing it just re-fires the POST.
          Cookies.set("b3_ref_seen", refSlug, { expires: 60, sameSite: "lax" });
          // Strip ?ref= ONLY after a successful track. Stripping eagerly
          // would lose the slug if the POST failed — a refresh wouldn't
          // retry because both the param and the b3_ref_seen marker are
          // gone, silently dropping the referral.
          if (typeof window !== "undefined") {
            const url = new URL(window.location.href);
            url.searchParams.delete("ref");
            window.history.replaceState({}, "", url.toString());
          }
        }
      })
      .catch(() => {
        /* noop — best-effort, leave ?ref= in place so refresh can retry */
      });
  }, [refSlug]);

  // 2. Post-signin claim: once the user is authenticated, POST
  //    /v1/referral/attribute. The b3_ref cookie is httpOnly so JS can't
  //    inspect it — we always fire and let the backend decide. The endpoint
  //    is idempotent (returns 204 on no-op), but Next.js layouts re-mount
  //    on every route navigation, so without dedup every page change by
  //    every signed-in user would fire a fresh POST.
  //
  //    Dedup combines the JWT prefix (changes per-account) with the
  //    `b3_ref_pending` companion cookie (set alongside b3_ref by the
  //    backend whenever a fresh referral is issued — short-link redirect
  //    or deep-link track). Without the pending cookie in the fingerprint,
  //    a user who claimed once on initial signin (returning 204 because no
  //    cookie existed) would skip the claim forever in that tab — even
  //    after they later click a /r/<slug> link. With it, a freshly-issued
  //    pending cookie produces a new fingerprint → claim re-fires →
  //    backend clears both cookies on success.
  //
  //    The empty `[]` dep array below is not the dedup; it just pins the
  //    effect to mount/unmount. The sessionStorage + cookie fingerprint
  //    is the actual dedup, surviving hot-reloads + layout remounts.
  useEffect(() => {
    if (typeof window === "undefined") return;
    // IMPORTANT: this read assumes B3_AUTH_COOKIE_NAME is NOT httpOnly. The
    // SDK's JS client also reads this cookie, so it has to remain visible to
    // JS today. If a future hardening pass flips the auth cookie to
    // httpOnly, Cookies.get() returns undefined and the claim flow silently
    // breaks — switch to a server-side claim trigger (Next.js route or
    // middleware) at that point.
    const jwt = Cookies.get(B3_AUTH_COOKIE_NAME);
    if (!jwt) return;
    const sessionKey = "b3_ref_claimed";
    const claimedFor = window.sessionStorage.getItem(sessionKey);
    // First 16 chars of a JWT are the base64-encoded header (algorithm +
    // typ), which is identical across every token signed with the same
    // algorithm — so `slice(0, 16)` collides for every account and the
    // sessionStorage guard would fire exactly once per tab regardless of
    // sign-out / sign-in. `slice(0, 64)` reaches into the payload, which
    // includes the `sub` claim (per-account user id), so the fingerprint
    // actually changes when the signed-in user changes.
    //
    // Companion cookie set by /r/<slug> + /v1/referral/track. A new
    // referral click overwrites this value, which busts the fingerprint
    // and causes the claim to re-fire. After a successful claim the
    // backend clears the cookie so subsequent re-mounts are no-ops again.
    const refPending = Cookies.get("b3_ref_pending") ?? "";
    const fingerprint = `${jwt.slice(0, 64)}|${refPending}`;
    if (claimedFor === fingerprint) return;
    // X-Org-ID is intentionally omitted: /v1/referral/attribute is registered
    // with `authMiddleware` only (not `orgMiddleware`) — see api_routes.go.
    // The handler reads user_id from auth context; org isn't needed for the
    // attribution UPSERT. If the route ever picks up `orgMiddleware`, also
    // forward the b3os-selected-orgId cookie value here as X-Org-ID, or
    // switch this call to authenticatedFetch (which adds both headers).
    fetch(`${API_BASE}/v1/referral/attribute`, {
      method: "POST",
      credentials: "include",
      headers: { Authorization: `Bearer ${jwt}` },
    })
      .then(res => {
        // Mark only on success or a *permanent* client error (404, 410,
        // 422, etc.). 401/403 mean "this token is unauthenticated /
        // unauthorized" — a sign-out → sign-in within the same tab needs
        // a fresh attempt, and the JWT-fingerprint guard already gives
        // every new sign-in one shot. 5xx is server-side and gets retried
        // on the next mount in the same tab.
        const isAuthFailure = res.status === 401 || res.status === 403;
        if (res.status < 500 && !isAuthFailure) {
          window.sessionStorage.setItem(sessionKey, fingerprint);
        }
      })
      .catch(() => {
        /* noop — best-effort, retry on next mount */
      });
  }, []);

  return null;
}
